/**
 * Contextual shimmer skeletons for the /calendar Social Automator module.
 * Pure layout placeholders — no user-facing copy (i18n ratchet).
 */
import React from 'react';
import { Skeleton } from '../../../../components/ui/Skeleton';

const cardClass = 'rounded-lg border border-gray-200 bg-white p-4 shadow-sm';

/** Overview / Content Type / Platform insight cards */
export function CalendarOverviewSkeleton(): React.ReactElement {
  return (
    <section aria-hidden className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={cardClass}>
          <Skeleton className="h-3 w-24" />
          <div className="mt-3 space-y-3">
            {Array.from({ length: 4 }).map((__, j) => (
              <div key={j} className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-5 w-8" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

/** Month/week grid: weekday labels + day cells with chip placeholders */
export function CalendarGridSkeleton({
  rows = 5,
  minHeight = 100,
}: {
  rows?: number;
  minHeight?: number;
}): React.ReactElement {
  return (
    <div
      className="overflow-hidden rounded-lg border border-gray-200"
      aria-hidden
      data-testid="calendar-grid-skeleton"
    >
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex justify-center px-2 py-3">
            <Skeleton className="h-3 w-8" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: rows * 7 }).map((_, i) => (
          <div
            key={i}
            className={`border-b border-gray-100 p-2 ${i % 7 !== 6 ? 'border-r' : ''}`}
            style={{ minHeight }}
          >
            <Skeleton className="mb-2 h-5 w-5 rounded-full" />
            <div className="mt-1.5 flex flex-wrap gap-1">
              <Skeleton className="h-5 w-10 rounded" />
              <Skeleton className="h-5 w-8 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Day agenda + scheduled post rows */
export function CalendarDaySkeleton(): React.ReactElement {
  return (
    <div
      className="max-w-xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
      aria-hidden
      data-testid="calendar-day-skeleton"
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
      <div className="space-y-3 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3">
            <Skeleton className="h-4 w-12 shrink-0" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Full calendar tab body while first fetch is in flight */
export function CalendarTabSkeleton({ view = 'month' }: { view?: 'day' | 'week' | 'month' }): React.ReactElement {
  return (
    <div role="status" aria-busy="true" data-testid="calendar-tab-skeleton">
      <CalendarOverviewSkeleton />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3" aria-hidden>
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-8 w-24 rounded-lg" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-40 rounded-full" />
          <Skeleton className="h-9 w-28 rounded-lg" />
        </div>
      </div>
      <div className="mb-3 flex gap-4" aria-hidden>
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-32" />
      </div>
      {view === 'day' ? (
        <CalendarDaySkeleton />
      ) : (
        <CalendarGridSkeleton rows={view === 'week' ? 1 : 5} minHeight={view === 'week' ? 340 : 100} />
      )}
    </div>
  );
}

/** Link in Bio editor — form + phone preview */
export function BuzzPageSkeleton(): React.ReactElement {
  return (
    <div
      className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between"
      role="status"
      aria-busy="true"
      data-testid="buzzpage-skeleton"
    >
      <div className="min-w-0 max-w-2xl flex-1 space-y-6" aria-hidden>
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <Skeleton className="mb-2 h-4 w-36" />
          <Skeleton className="h-3 w-64 max-w-full" />
        </div>
        <div className="space-y-3">
          <div className="space-y-2">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3 w-16" />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      </div>
      <div className="flex w-full flex-col items-center lg:w-[280px] lg:shrink-0" aria-hidden>
        <Skeleton className="mb-3 h-3 w-16" />
        <Skeleton className="h-[460px] w-full max-w-[260px] rounded-[1.75rem]" />
      </div>
    </div>
  );
}

/** Comment replies list */
export function CommentRulesSkeleton(): React.ReactElement {
  return (
    <div className="max-w-3xl space-y-4" role="status" aria-busy="true" data-testid="comment-rules-skeleton">
      <div className="flex items-start justify-between gap-3" aria-hidden>
        <div className="space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-72 max-w-full" />
        </div>
        <Skeleton className="h-9 w-28 rounded-lg" />
      </div>
      <div className="space-y-2" aria-hidden>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
            <Skeleton className="h-4 w-4 shrink-0 rounded" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-56 max-w-full" />
            </div>
            <Skeleton className="h-6 w-11 shrink-0 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Performance summary + post list */
export function PerformanceSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4" role="status" aria-busy="true" data-testid="performance-skeleton">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-hidden>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className={cardClass}>
            <Skeleton className="mb-2 h-3 w-16" />
            <Skeleton className="h-7 w-14" />
          </div>
        ))}
      </div>
      <div className="space-y-2" aria-hidden>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
            <Skeleton className="h-12 w-12 shrink-0 rounded-md" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
            <Skeleton className="h-8 w-20 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Comments drawer body */
export function CommentsDrawerSkeleton(): React.ReactElement {
  return (
    <div className="space-y-3 p-4" role="status" aria-busy="true" data-testid="comments-drawer-skeleton">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2 border-b border-gray-100 pb-3" aria-hidden>
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6 rounded-full" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}
